import React, { useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSButton from "../../system-design/DSButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import useClocksInfiniteQuery from "../../Device/hooks/useClocksInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../Device/hooks/useListboxInfiniteScroll";
import { ClocksListResponse } from "../../../Types";

interface AssignSerialDialogProps {
  open: boolean;
  title: string;
  excludeClockIds: string[];
  onClose: () => void;
  onAssign: (clockId: string) => void;
}

const AssignSerialDialog = ({
  open,
  title,
  excludeClockIds,
  onClose,
  onAssign,
}: AssignSerialDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClock, setSelectedClock] = useState<ClocksListResponse | null>(
    null,
  );

  const {
    data: availableClocks,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({
    query: searchQuery,
    registrationStatus: "ALL",
  });

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const filteredClocks = useMemo(() => {
    if (!availableClocks) return [];
    const excluded = new Set(excludeClockIds);
    return availableClocks.filter((clock) => !excluded.has(clock.id));
  }, [availableClocks, excludeClockIds]);

  const handleAssign = () => {
    if (!selectedClock) return;
    onAssign(selectedClock.id);
    setSelectedClock(null);
    setSearchQuery("");
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>{title}</DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ pt: 1 }}>
          <Autocomplete
            options={filteredClocks}
            filterOptions={noClientFilter}
            getOptionLabel={(option) => `${option.idf} (${option.id})`}
            value={selectedClock}
            onChange={(_, newValue) => setSelectedClock(newValue)}
            inputValue={searchQuery}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            loading={isLoading || isFetchingNextPage}
            ListboxProps={listboxProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by serial, friendly ID..."
                variant="outlined"
                size="small"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading && <DSCircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <DSBox>
                  <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                    {option.idf}
                  </DSTypography>
                  <DSTypography variant="caption" color="text.secondary">
                    ID: {option.id}
                  </DSTypography>
                </DSBox>
              </li>
            )}
            noOptionsText={
              searchQuery ? "No clocks found" : "Type to search for clocks"
            }
          />
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Cancel</DSSecondaryButton>
        <DSButton
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedClock}
        >
          Assign
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default AssignSerialDialog;
